import React, { useEffect, useState } from "react";
import "../styles/home.css";
import "../styles/table.css";
import "../styles/theme.css";
import Layout from "./Layout";
import { getTransactionData } from "../functions/api/transactions";
import TransactionTable from "./display/TableComponent";
import { getReceivesLoads, getReceivesLoadsAndLevels, getReceiveItems } from "../functions/api/receive";
import { Code } from "@mui/icons-material";
import DropdownComponent from "./display/DropDownComponent";
import ReusableLineChart from "./display/LineChartComponent";
import VolumeChart from "./display/DualChartComponent";
import SmallTableComponent from "./display/SmallTable";
import SmallTable from "./display/SmallTable";
import DescriptionList from "./display/DetailsComponent";


const getYesterday = (): string => {
    const date = new Date();
    date.setDate(date.getDate() - 1);
    return date.toISOString().slice(0, 10); // YYYY-MM-DD
};

const getToday = (): string => {
    const date = new Date();
    date.setDate(date.getDate());
    return date.toISOString().slice(0, 10); // YYYY-MM-DD
};

const ReceivesComponent: React.FC = () => {
    // Receive Loads data handling
    const [receivesData, setReceivesData] = useState<any[]>([]);
    const [selectedFromDate, setSelectedFromDate] = useState<string>(getYesterday());
    const [selectedToDate, setSelectedToDate] = useState<string>(getToday);
    const [code, setSelectedCode] = useState<string>('Lusaka-Receive');

    // Loading display - TODO: Replace with spinner
    const [loading, setLoading] = useState<boolean>(true);

    // Date for Receive Loads and Levels data call
    // Linked to SelectedFromDate for now
    const [receivesAndLevelsData, setReceivesAndLevelsData] = useState<any[]>([]);
    const [siteOptions, setSiteOptions] = useState<any[]>([]);
    const [selectedDate, setSelectedDate] = useState<string>(selectedFromDate);
    const [sectionBLoading, setSectionBLoading] = useState<boolean>(true);


    // Dropdown list for Receive Items

    const populateReceiveItems = async() =>{
    const data =  await getReceiveItems();

        if (Array.isArray(data)) {
            setSiteOptions(data);
        } else {
            setSiteOptions([]);
        }
    // console.log(siteOptions)
    };

    // ["Lusaka-Receive", "Ndola-Receive", "Chingola-Receive", "Kafue-Receive"];

    const loadReceiveLoadsData = async (code: string, fromDate: string, toDate: string) => {
        setLoading(true);
        const data = await getReceivesLoads(code, fromDate, toDate);
        if (Array.isArray(data)) {
            setReceivesData(data);
        } else {
            setReceivesData([]);
        }
        setLoading(false);
    };


    const loadReceiveLoadsAndLevelsData = async (code: string, date: string) => {
      setSectionBLoading(true);
      const data = await getReceivesLoadsAndLevels(code, date);
    //   console.log(data)
    
      if (Array.isArray(data)) {
        // Deduplicate based on composite key
        const seen = new Map<string, any>();
        data.forEach(item => {
          const key = `${item.tank_code}-${item.nearbyTime}-${item.tank_gross_volume}`;
          if (!seen.has(key)) {
            seen.set(key, item);
          }
        });
    
        const deduplicatedData = Array.from(seen.values());
        setReceivesAndLevelsData(deduplicatedData);
      } else {
        setReceivesAndLevelsData([]);
      }
  
      setSectionBLoading(false);
    };


    useEffect(() => {
        populateReceiveItems();
        loadReceiveLoadsData(code, selectedFromDate, selectedToDate);
        loadReceiveLoadsAndLevelsData(code, selectedDate);
    }, []);


    const handleFromDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newDate = e.target.value;
        setSelectedFromDate(newDate);
        loadReceiveLoadsData(code, newDate, selectedToDate);
    };


    const handleToDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newDate = e.target.value;
        setSelectedToDate(newDate);
        loadReceiveLoadsData(code, selectedFromDate, newDate);
    };

    const handleDropdownChange = (newCode: string) => {
        setSelectedCode(newCode);
        loadReceiveLoadsData(newCode, selectedFromDate, selectedToDate);
        loadReceiveLoadsAndLevelsData(newCode, selectedDate);
    };


    const handleLevelsDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newDate = e.target.value;
        setSelectedDate(newDate);
        loadReceiveLoadsAndLevelsData(code, newDate);
    };


    const cleanedLevelsWithTotal = (() => {
    const firstBaseTime = receivesAndLevelsData.find(item => item.baseTime)?.baseTime;
    if (!firstBaseTime) return [];

    const base = new Date(firstBaseTime);

    const levelsByTank = receivesAndLevelsData.reduce<Record<string, typeof receivesAndLevelsData[0]>>((acc, item) => {
      if (!item.nearbyTime || !item.baseTime) return acc;

      const nearby = new Date(item.nearbyTime);
      const diffMinutes = (base.getTime() - nearby.getTime()) / (1000 * 60);

      if (diffMinutes > 30 && diffMinutes <= 60) {
        const key = item.tank_code;
        if (!acc[key] || nearby > new Date(acc[key].nearbyTime)) {
          acc[key] = item;
        }
      }
      return acc;
    }, {});

    const cleaned = Object.values(levelsByTank).map(({ baseTime, endTime, receive_gross_volume, receive_gst_volume, ...rest }) => {
      const { tank_code, ...others } = rest;
      return { tank_code, ...others }; // Reorder: tank_code first
    });

    const totalRow = cleaned.reduce((acc, row) => {
      for (const key in row) {
        const value = row[key];
        if (typeof value === "number") {
          acc[key] = (acc[key] || 0) + value;
        } else {
          acc[key] = key === "tank_code" ? "Total" : "";
        }
      }
      return acc;
    }, {} as typeof cleaned[0]);

      return [...cleaned, totalRow];
    })();

    return (
        <Layout>
            <div className="home-container" style={{ overflowX: "auto" }}>
                <h2 className="home-header">Receive Transactions</h2>
                <DescriptionList text="
                                This report shows receives/offloads per site, as well as their tank responses when receiving product.
                                /nYou can choose a date range where you are interested in seeing if there was an offloading that occurred.
                                /nIf there was an offloading that occurred a table will be populated with those days that had an offload with their necessary information.
                                /nYou can then analyse deeper into that day that had an offload if you select that date with the third date selector - here you can see each tanks behaviour for that offload/receive point in time.
                                /nYou will see the dips for each tank 30 minutes before the offload occurs, as this is recorded on site.
                                /n* You can then click the Analyse button that will perform a peak-valley analysis: Take tank heighest volume in the future minus lowest past value to evaluate agains the received volume amount.
                                /n* The peak-valley analysis and graphical representations are in gross and GST.
                                " 
                />
                
                {/* Receive Loads Section */}
                <div>


                    <div  className="dropdown">
                        <DropdownComponent
                            options={siteOptions}
                            value={code}
                            onChange={handleDropdownChange}
                            label="Choose code"
                        />
                        {code && <p className="small">You selected: {code}</p>}
                    </div>

                    {/* RECEIVE LOADS: From Date */}
                    <div style={{ marginBottom: "1rem" }}>
                        <label style={{ marginRight: "0.5rem" }}>From:</label>
                        <div className="date-picker-wrapper">

                    {/* Left Arrow */}
                    <button className="date-arrow" onClick={() => {
                        const prev = new Date(selectedFromDate);
                        prev.setDate(prev.getDate() - 1);
                        handleFromDateChange({
                            target: { value: prev.toISOString().slice(0, 10) }
                        } as React.ChangeEvent<HTMLInputElement>);
                    }}
                    >←
                    </button>

                        <input
                            type="date"
                            value={selectedFromDate}
                            onChange={handleFromDateChange}
                            style={{ padding: "0.4rem" }}
                        />
                    {/* Right Arrow */}
                    <button className="date-arrow" onClick={() => {
                        const next = new Date(selectedFromDate);
                        next.setDate(next.getDate() + 1);
                        handleFromDateChange({
                            target: { value: next.toISOString().slice(0, 10) }
                        } as React.ChangeEvent<HTMLInputElement>);
                    }}
                    >→
                    </button>
                    </div>
                    </div>

                    {/* RECEIVE LOADS: To Date */}
                    <div style={{ marginBottom: "1rem" }}>
                        <label style={{ marginRight: "0.5rem" }}>To:</label>
                        <div className="date-picker-wrapper">
                           
                        {/* Left Arrow */}
                        <button className="date-arrow" onClick={() => {
                            const prev = new Date(selectedToDate);
                            prev.setDate(prev.getDate() - 1);
                            handleToDateChange({
                                target: { value: prev.toISOString().slice(0, 10) }
                            } as React.ChangeEvent<HTMLInputElement>);
                        }}
                        >←
                        </button>

                        <input
                            type="date"
                            value={selectedToDate}
                            onChange={handleToDateChange}
                            style={{ padding: "0.4rem" }}
                        />
                      
                        {/* Right Arrow */}
                        <button className="date-arrow" onClick={() => {
                            const next = new Date(selectedToDate);
                            next.setDate(next.getDate() + 1);
                            handleToDateChange({
                                target: { value: next.toISOString().slice(0, 10) }
                            } as React.ChangeEvent<HTMLInputElement>);
                        }}
                        >→
                        </button>
                        </div>
                    </div>
                    <h2 className="normal-heading">Receive Loads</h2>

                    {loading ? (
                        <p>Loading...</p>
                    ) : receivesData.length > 0 ? (
                        <div className="table-scroll-wrapper">
                            <div className="table-scroll-inner">
                                <SmallTableComponent data={receivesData} />
                            </div>
                        </div>
                    ) : (
                        <p>No Receive Loads found for {selectedFromDate} to {selectedToDate}.</p>
                    )}
                </div>

                {/* Receive Loads and Levels Section */}

                <div>
                    <h2 className="normal-heading">Receive Loads and Levels</h2>

                    <div style={{ marginBottom: "1rem" }}>
                        <label style={{ marginRight: "0.5rem" }}>From:</label>
                        <div className="date-picker-wrapper">

                        {/* Left Arrow */}
                        <button className="date-arrow" onClick={() => {
                            const prev = new Date(selectedDate);
                            prev.setDate(prev.getDate() - 1);
                            handleLevelsDateChange({
                                target: { value: prev.toISOString().slice(0, 10) }
                            } as React.ChangeEvent<HTMLInputElement>);
                        }}
                        >←
                        </button>

                        <input
                            type="date"
                            value={selectedDate}
                            onChange={handleLevelsDateChange}
                            style={{ padding: "0.4rem" }}
                        />

                        {/* Right Arrow */}
                        <button className="date-arrow" onClick={() => {
                            const next = new Date(selectedDate);
                            next.setDate(next.getDate() + 1);
                            handleLevelsDateChange({
                                target: { value: next.toISOString().slice(0, 10) }
                            } as React.ChangeEvent<HTMLInputElement>);
                        }}
                        >→
                        </button>
                    </div>
                    </div>

                    {sectionBLoading ? (
                        <p>Loading...</p>
                    ) : receivesAndLevelsData.length > 0 ? (
                        <div className="table-scroll-wrapper">
                            <div className="table-scroll-inner">
                                <TransactionTable data={receivesAndLevelsData} />
                            </div>
                              {/* Show Dips before Offload */}
                              {cleanedLevelsWithTotal.length > 0 && (
                                  <div>
                                    <h2 className="normal-heading">Tank volumes around 30 minutes before first Offload ({selectedDate.toLocaleString()})</h2> 
                                    <SmallTable data={cleanedLevelsWithTotal} 
                                    showSearchControls = {false} 
                                    rowClassName={(row, index, data) => index === data.length - 1 ? "summary-row" : ""}
                                    />
                                  </div>
                                )}
                            <div>
                                {Object.entries(
                                    receivesAndLevelsData.reduce<Record<string, any[]>>((acc, item) => {
                                        const key = String(item.tank_code);
                                        if (!acc[key]) acc[key] = [];
                                        acc[key].push(item);
                                        return acc;
                                    }, {})
                                ).map(([tankCode, dataForTank]) => (
                                    <div key={tankCode} style={{ marginBottom: "50px" }}>
                                        <h3 className="normal-heading">Tank Name: {tankCode}</h3>

                                        <VolumeChart data={dataForTank} tankCode={tankCode} />

                                    </div>
                                ))}
                            </div>
                        </div>
                    ) : (
                        <p>No Receive Loads and Levels found for {selectedDate}.</p>
                    )}
                </div>
            </div>
        </Layout>
    );
};

export default ReceivesComponent;
